import { Button } from "@package/subframe/components/Button";
import { IconButton } from "@package/subframe/components/IconButton";
import { FeatherX } from "@subframe/core";
import type { ReactNode } from "react";

import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

interface ProfileSectionDialogProps {
  title: string;
  testId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isPending: boolean;
  onSubmit: () => void;
  children: ReactNode;
}

/**
 * The frame every section's edit dialog shares. The dialog renders outside the
 * masked profile page, so it carries `ph-no-capture sentry-block` itself.
 */
export function ProfileSectionDialog({
  title,
  testId,
  open,
  onOpenChange,
  isPending,
  onSubmit,
  children,
}: ProfileSectionDialogProps) {
  // Kept open until the save settles, so its error can't land on a closed dialog.
  const handleOpenChange = (next: boolean) => {
    if (!next && isPending) return;
    onOpenChange(next);
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    onSubmit();
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        showCloseButton={false}
        className="ph-no-capture sentry-block max-h-[90vh] max-w-lg overflow-y-auto p-0"
      >
        <DialogTitle className="sr-only">{title}</DialogTitle>

        <form
          onSubmit={handleSubmit}
          noValidate
          className="flex flex-col"
          data-testid={testId}
        >
          <div className="flex items-center justify-between border-b border-solid border-secondary-beige-200 px-6 py-4">
            <span className="text-heading-2 font-heading-2 text-default-font">
              {title}
            </span>
            <IconButton
              variant="neutral-tertiary"
              size="medium"
              icon={<FeatherX />}
              aria-label="Close"
              disabled={isPending}
              onClick={() => handleOpenChange(false)}
            />
          </div>

          <div className="flex flex-col gap-6 px-6 py-6">{children}</div>

          <div className="flex items-center justify-end gap-3 border-t border-solid border-secondary-beige-200 px-6 py-4">
            <Button
              type="button"
              variant="neutral-primary"
              size="large"
              onClick={() => handleOpenChange(false)}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="brand-primary"
              size="large"
              loading={isPending}
              disabled={isPending}
            >
              Save changes
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
